import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { createEditor } from 'slate';
import { Slate, withReact } from 'slate-react';
import { withHistory } from 'slate-history';

import { TranslatedTooltipWrapper } from 'src/components/TranslatedTooltipWrapper';
import { useDecoratePositionedMarks } from 'src/components/RichSlate';
import { applyEditorTextChanges } from 'src/lib/slate';
import MarkedEditable from './components/MarkedEditable';
import { SlateErrorBoundary } from './components/SlateErrorBoundary';
import { withDisabledNewlines, withMarking } from './utils';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  value: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onFocusChange: PropTypes.func,
  markUserInput: PropTypes.bool,
  disableNewlines: PropTypes.bool,
  marks: PropTypes.arrayOf(PropTypes.shape()),
  hideMarks: PropTypes.bool,
  addons: PropTypes.arrayOf(PropTypes.elementType),
  setEditor: PropTypes.func,
  disableChildrenRewrite: PropTypes.bool,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
};

const defaultProps = {
  className: null,
  classes: null,
  readOnly: false,
  onFocusChange: null,
  markUserInput: true,
  disableNewlines: false,
  marks: [],
  hideMarks: false,
  addons: [],
  setEditor: null,
  disableChildrenRewrite: false,
  onCopy: null,
  onCut: null,
};

const MarkedEditor = forwardRef(
  (
    {
      className,
      classes,
      value,
      readOnly,
      onChange,
      onFocusChange,
      markUserInput,
      disableNewlines,
      marks,
      hideMarks,
      addons,
      setEditor,
      disableChildrenRewrite,
      onCopy,
      onCut,
      ...restProps
    },
    ref
  ) => {
    const editor = useMemo(() => {
      // Plain editor + react wrapper
      let result = withReact(createEditor());

      // If we want to mark all user edits - wrap in custom plugin
      if (markUserInput) {
        result = withMarking(result);
      }

      if (disableNewlines) {
        result = withDisabledNewlines(result);
      }

      // Support undo-redo
      result = withHistory(result);

      return result;
    }, [markUserInput, disableNewlines]);

    const decorate = useDecoratePositionedMarks(editor, marks);

    useImperativeHandle(
      ref,
      () => ({
        applyTextChanges: (changes) => {
          applyEditorTextChanges(editor, changes);
        },
      }),
      [editor]
    );

    // "The Slate Provider's "value" prop is now only used as initial state for editor.children as was intended before.
    //  If your code relies on replacing editor.children you should do so by replacing it directly instead of relying on the "value" prop to do this for you."
    // https://github.com/ianstormtaylor/slate/releases/tag/slate-react%400.67.0
    // https://github.com/ianstormtaylor/slate/pull/4540
    // https://github.com/ianstormtaylor/slate/issues/4710
    if (!disableChildrenRewrite) {
      editor.children = value;
    }

    useEffect(() => {
      setEditor && setEditor(editor);
    }, [editor, setEditor]);

    return (
      <TranslatedTooltipWrapper>
        <SlateErrorBoundary>
          <Slate
            editor={editor}
            value={value}
            onChange={onChange}
            data-testid="MarkedEditor__Slate"
          >
            <MarkedEditable
              className={cx(className)}
              classes={classes}
              readOnly={readOnly}
              decorate={decorate}
              hideMarks={hideMarks}
              onFocusChange={onFocusChange}
              {...restProps}
              onCopy={(e) => {
                if (onCopy) {
                  onCopy(editor);
                  e.preventDefault();
                }
              }}
              onCut={() => {
                // oncut that is passed here is actually a copy handler so we should not prevent default
                if (onCut) {
                  onCut(editor);
                }
              }}
            />
            {addons.map((Addon) => (
              <Addon />
            ))}
          </Slate>
        </SlateErrorBoundary>
      </TranslatedTooltipWrapper>
    );
  }
);

MarkedEditor.propTypes = propTypes;
MarkedEditor.defaultProps = defaultProps;

export default MarkedEditor;
